import { useAuthStore } from '../../features/auth/store';

/**
 * PE-2147 — claim-based permission check.
 *
 * `user.permissions` is the JWT-derived union watchman's permission-resolver
 * builds for the session: the role's grants + per-user overrides + every
 * department-role grant from `staffAssignments`, keyed by watchman's uppercase
 * `Resource` → `ResourceAction[]` vocabulary (`{ MERCHANT_ONBOARDING: ['MANAGE'] }`).
 *
 * This is deliberately separate from `usePermissions().hasPermission`, which
 * authorises off the static per-roleName map in `features/auth/permissions.ts`
 * (a different, kebab-case vocabulary that also covers surfaces with no watchman
 * Resource). Use this hook where the gate is a real watchman resource that a
 * department role can grant — it is the only check that sees department grants.
 */
export const hasClaimPermission = (
  permissions: Record<string, string[]> | undefined | null,
  resource: string,
  action: string,
): boolean => {
  // Exact match only: both permission guards (watchman + osprey-api) test the
  // literal action, so MANAGE does not imply READ/UPDATE here either.
  const actions = permissions?.[resource];
  return Array.isArray(actions) && actions.includes(action);
};

export const useClaimPermission = () => {
  const permissions = useAuthStore((st) => st.user?.permissions);
  return {
    hasClaim: (resource: string, action: string) => hasClaimPermission(permissions, resource, action),
  };
};
